import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@design-system';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import {
  CARRIER_GUIDELINES,
  PublishDialog,
  VersionHistoryTable,
  useDiscardUnderwritingPromptDraft,
  usePublishUnderwritingPromptDraft,
  useSaveUnderwritingPromptDraft,
  useUnderwritingPromptVersion,
  useUnderwritingPromptVersions,
  useUpdateUnderwritingPromptDraft,
} from '@/features/wizard-underwriting-guidelines';
import { useBeforeUnloadWarning } from '@/hooks/useBeforeUnloadWarning';

export function WizardUnderwritingGuidelineEditorPage() {
  const { promptKey } = useParams<{ promptKey: string }>();
  const carrier = CARRIER_GUIDELINES.find(c => c.promptKey === promptKey);

  const versionsQuery = useUnderwritingPromptVersions(promptKey ? { promptKey } : undefined);
  const versions = versionsQuery.data ?? [];
  const publishedSummary = versions.find(v => v.status === 'published') ?? null;
  const draftSummary = versions.find(v => v.status === 'draft') ?? null;

  const publishedQuery = useUnderwritingPromptVersion(publishedSummary?.id ?? null);
  const draftQuery = useUnderwritingPromptVersion(draftSummary?.id ?? null);

  const baseContent = draftQuery.data?.content ?? publishedQuery.data?.content ?? '';
  const [content, setContent] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [isOpeningPublish, setIsOpeningPublish] = useState(false);

  useEffect(() => {
    if (!isInitialized && (publishedQuery.data || draftQuery.data)) {
      setContent(baseContent);
      setIsInitialized(true);
    }
  }, [baseContent, isInitialized, publishedQuery.data, draftQuery.data]);

  const isDirty = isInitialized && content !== baseContent;
  useBeforeUnloadWarning(isDirty);

  const saveDraft = useSaveUnderwritingPromptDraft();
  const updateDraft = useUpdateUnderwritingPromptDraft();
  const discardDraft = useDiscardUnderwritingPromptDraft();
  const publishDraft = usePublishUnderwritingPromptDraft();

  const isSaving = saveDraft.isPending || updateDraft.isPending;
  const publishedContent = publishedQuery.data?.content ?? '';

  const onSaveDraft = async () => {
    if (!promptKey) return;
    setErrorMessage(null);
    try {
      if (draftSummary) {
        await updateDraft.mutateAsync({ draftId: draftSummary.id, content });
      } else {
        await saveDraft.mutateAsync({ promptKey, content });
      }
    } catch (e: any) {
      setErrorMessage(formatError(e));
    }
  };

  const onDiscardDraft = async () => {
    if (!draftSummary) return;
    setErrorMessage(null);
    try {
      await discardDraft.mutateAsync({ draftId: draftSummary.id });
      setContent(publishedQuery.data?.content ?? '');
    } catch (e: any) {
      setErrorMessage(formatError(e));
    }
  };

  const onOpenPublish = async () => {
    if (!promptKey) return;
    setErrorMessage(null);
    setIsOpeningPublish(true);
    try {
      // Save current content as draft first so the published version reflects what the editor sees.
      if (isDirty) {
        if (draftSummary) {
          await updateDraft.mutateAsync({ draftId: draftSummary.id, content });
        } else {
          await saveDraft.mutateAsync({ promptKey, content });
        }
      }
      setPublishOpen(true);
    } catch (e: any) {
      setErrorMessage(formatError(e));
    } finally {
      setIsOpeningPublish(false);
    }
  };

  const onPublish = async (notes: string) => {
    const id = draftSummary?.id ?? saveDraft.data?.id ?? updateDraft.data?.id;
    if (!id) {
      setErrorMessage('No draft to publish.');
      return;
    }
    try {
      await publishDraft.mutateAsync({ draftId: id, notes });
      setPublishOpen(false);
      setIsInitialized(false); // re-init from new published row
    } catch (e: any) {
      setErrorMessage(formatError(e));
    }
  };

  const draftContentForDialog = useMemo(() => content, [content]);

  if (!carrier) {
    return (
      <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="text-sm text-red-600">Unknown carrier: {promptKey}</div>
        <Link
          to="/wizard/underwriting-guidelines"
          className="text-primary text-sm"
        >
          Back to list
        </Link>
      </main>
    );
  }

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <Link
        to="/wizard/underwriting-guidelines"
        className="text-primary mb-2 inline-flex items-center text-sm hover:underline"
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        All carriers
      </Link>
      <DashboardPageHeader
        title={carrier.carrierName}
        description={`Editing ${carrier.promptKey}`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-gray-700">
        {publishedSummary ? (
          <span>
            Published <span className="font-mono">v{publishedSummary.versionNumber}</span>
            {publishedSummary.publishedAt &&
              ` · ${new Date(publishedSummary.publishedAt).toLocaleString()}`}
            {publishedSummary.publishedByName && ` · ${publishedSummary.publishedByName}`}
          </span>
        ) : (
          <span className="text-amber-700">No published version yet</span>
        )}
        {draftSummary && (
          <span className="text-amber-700">
            Draft <span className="font-mono">v{draftSummary.versionNumber}</span> in progress
          </span>
        )}
        {isDirty && <span className="text-amber-700">Unsaved changes</span>}
      </div>

      <textarea
        className="focus:border-primary focus:ring-primary block h-[60vh] w-full rounded border border-gray-300 p-3 font-mono text-xs leading-5 focus:outline-none focus:ring-1"
        value={content}
        onChange={e => setContent(e.target.value)}
        spellCheck={false}
        placeholder="YAML guideline content"
      />

      {errorMessage && (
        <div className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{errorMessage}</div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={onSaveDraft}
          disabled={!isDirty || isSaving}
        >
          {isSaving && !isOpeningPublish && <Loader2 className="h-4 w-4 animate-spin" />}
          {isSaving && !isOpeningPublish ? 'Saving…' : 'Save draft'}
        </Button>
        <Button
          variant="outline"
          onClick={onDiscardDraft}
          disabled={!draftSummary || discardDraft.isPending}
        >
          Discard draft
        </Button>
        <Button
          variant="outline"
          onClick={onOpenPublish}
          disabled={(!draftSummary && !isDirty) || isSaving || publishDraft.isPending}
        >
          {(isOpeningPublish || publishDraft.isPending) && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          Publish
        </Button>
      </div>

      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        carrierName={carrier.carrierName}
        draftContent={draftContentForDialog}
        publishedContent={publishedContent}
        isPending={publishDraft.isPending}
        errorMessage={errorMessage}
        onPublish={onPublish}
      />

      <div className="mt-10">
        <h2 className="mb-3 text-base font-semibold">Version history</h2>
        <VersionHistoryTable versions={versions} />
      </div>
    </main>
  );
}

function formatError(err: any): string {
  const data = err?.response?.data;
  if (data?.message) {
    const meta = data.metadata;
    if (meta?.line)
      return `${data.message} (line ${meta.line}${meta.column ? `, col ${meta.column}` : ''})`;
    return data.message;
  }
  return err?.message ?? 'Something went wrong';
}
